/**
 * YouScreen.jsx — Phase 6 (FIXED)
 *
 * BUGS FIXED:
 * 1. CRASH: "Objects are not valid as a React child"
 *    getStreak() returns {current, best, lastCompleted} — an object, not a number.
 *    Was rendered directly as {roadmap.streak} in two JSX spots.
 *    Fix: extract roadmap.streak.current (or roadmap.streak if already a number as fallback).
 *
 * 2. getStats() may also return an object with nested structure — guarded all
 *    stats fields with optional chaining and Number() coercion.
 *
 * 3. showRoadmap overlay used position:"fixed" with inset:0 but no top safe-area
 *    padding, causing content to hide under the notch on iOS.
 *    Fix: added paddingTop with env(safe-area-inset-top).
 *
 * 4. Roadmap overlay back button used inline var(--bg-color) / var(--primary-color)
 *    which are desktop CSS variables that don't exist in the PWA shell.
 *    Fix: switched to PWA shell variables (var(--card), var(--brand), var(--sand-light)).
 *
 * 5. hh-focusroom-overlay class on roadmap overlay — that class doesn't exist
 *    in mobile.css (it's a home-screen class). Changed to a plain div with
 *    explicit styles so it doesn't accidentally inherit wrong styles.
 *
 * Logic / structure: fully preserved from original.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { getDoc, doc, deleteDoc } from "firebase/firestore";
import { signOut, deleteUser } from "firebase/auth";
import { useAppShell } from "../AppShellContext";
import { hasActiveRoadmap, getConfig, getTodayDay, getStats, getStreak } from "../../components/RoadmapEngine";
import Roadmap from "../../pages/Roadmap";

const WELLNESS_KEY = "healhub_wellness_v3";

function loadAssessmentHistory() {
  try {
    const r = localStorage.getItem(WELLNESS_KEY);
    return r ? JSON.parse(r) : {};
  } catch {
    return {};
  }
}

// ─── Helper: safely extract a numeric streak value ────────────────────────────
// getStreak() can return either a plain number OR {current, best, lastCompleted}.
// Rendering an object directly crashes React, so always coerce to a number here.
function extractStreakNumber(streakValue) {
  if (typeof streakValue === "number") return streakValue;
  if (streakValue && typeof streakValue === "object") {
    // Prefer .current, fall back to .best, fall back to 0
    return Number(streakValue.current ?? streakValue.best ?? 0);
  }
  return 0;
}

const ASSESSMENTS = [
  { id: "gad7",  emoji: "😰", name: "Anxiety (GAD-7)",   color: "#e8a135" },
  { id: "phq9",  emoji: "😔", name: "Depression (PHQ-9)", color: "#7B8F8A" },
  { id: "pss",   emoji: "😓", name: "Stress (PSS)",       color: "#E6A184" },
  { id: "sleep", emoji: "😴", name: "Sleep Quality",      color: "#5A6993" },
];

export default function YouScreen() {
  const navigate = useNavigate();
  const { user, isDark, toggleDark, setActiveTab, resetTrigger, activeTab } = useAppShell();

  const [profile,       setProfile]      = useState(null);
  const [streak,        setStreak]       = useState(0);
  const [daysSince,     setDaysSince]    = useState(0);
  const [assessHistory, setAssessHist]   = useState({});
  const [roadmap,       setRoadmap]      = useState(null);
  const [showRoadmap,   setShowRoadmap]  = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm,   setDeleteConfirm]   = useState("");
  const [deleting,        setDeleting]        = useState(false);
  const [deleteError,     setDeleteError]     = useState("");

  // Reset listener — closes modals when the profile icon is double-clicked
  useEffect(() => {
    if (activeTab === "you") {
      setShowRoadmap(false);
      setShowDeleteModal(false);
    }
  }, [resetTrigger, activeTab]);

  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) setProfile(snap.data());
      } catch { /* silent */ }
    })();

    setStreak(parseInt(localStorage.getItem("streakCount")) || 0);
    setAssessHist(loadAssessmentHistory());

    if (user.metadata?.creationTime) {
      setDaysSince(
        Math.floor((Date.now() - new Date(user.metadata.creationTime).getTime()) / 86400000)
      );
    }

    if (hasActiveRoadmap()) {
      const config   = getConfig();
      const stats    = getStats();
      const todayDay = getTodayDay();

      // FIX 1: getStreak() may return an object — always extract a number
      const rawStreak    = getStreak();
      const streakNumber = extractStreakNumber(rawStreak);

      setRoadmap({ config, stats, todayDay, streak: streakNumber });
    }
  }, [user]);

  const handleLogout = useCallback(async () => {
    try { await signOut(auth); navigate("/"); }
    catch { alert("Failed to log out."); }
  }, [navigate]);

  const handleDeleteAccount = useCallback(async () => {
    if (deleteConfirm !== "DELETE" || !user) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteDoc(doc(db, "users", user.uid));
      await deleteUser(user);
      localStorage.clear();
      sessionStorage.clear();
      navigate("/");
    } catch (err) {
      if (err.code === "auth/requires-recent-login")
        setDeleteError("For security, please log out and log back in, then try again.");
      else
        setDeleteError("Failed to delete account. Please try again.");
      setDeleting(false);
    }
  }, [deleteConfirm, user, navigate]);

  const firstName = profile?.basicInfo?.firstName
    || profile?.profile?.firstName
    || user?.displayName?.split(" ")[0]
    || "User";
  const lastName = profile?.basicInfo?.lastName || profile?.profile?.lastName || "";
  const initial  = firstName.charAt(0).toUpperCase();
  const joinDate = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : "";

  // FIX 2: safely read stats — getStats() shape may vary
  const completedDays = Number(roadmap?.stats?.completed ?? roadmap?.stats?.completedDays ?? 0);
  const planLength    = Number(roadmap?.config?.planLength ?? 30);
  const todayDayNum   = Number(roadmap?.todayDay ?? 1);
  const progressPct   = Math.min(100, (todayDayNum / planLength) * 100);

  return (
    <div className="hh-you">

      {/* ═══ PROFILE HERO ═══ */}
      <div className="hh-profile-hero">
        <div className="hh-profile-avatar">{initial}</div>
        <div className="hh-profile-name">{firstName} {lastName}</div>
        <div className="hh-profile-sub">{user?.email} · Joined {joinDate}</div>
        <div className="hh-profile-stats">
          <div className="hh-pstat">
            {/* FIX: streak here is already a plain number from useState(0) */}
            <div className="hh-pstat-n">{streak}</div>
            <div className="hh-pstat-l">Streak</div>
          </div>
          <div className="hh-pstat">
            <div className="hh-pstat-n">{daysSince}</div>
            <div className="hh-pstat-l">Days</div>
          </div>
          <div className="hh-pstat">
            <div className="hh-pstat-n">{Object.keys(assessHistory).length}</div>
            <div className="hh-pstat-l">Tests</div>
          </div>
        </div>
      </div>

      {/* ═══ RECOVERY ROADMAP ═══ */}
      {roadmap ? (
        <div className="hh-you-section">
          <div className="hh-section-label">🗺️ recovery roadmap</div>
          <div
            className="hh-card hh-roadmap-card"
            onClick={() => setShowRoadmap(true)}
            role="button"
            tabIndex={0}
          >
            <div className="hh-rm-title">{roadmap.config?.name || "Your Journey"}</div>
            {/* FIX: todayDayNum and planLength are Numbers — safe to render */}
            <div className="hh-rm-sub">Day {todayDayNum} of {planLength}</div>
            <div className="hh-rm-progress-track">
              <div className="hh-rm-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="hh-rm-stats">
              {/* FIX: roadmap.streak is now always a number (extracted above) */}
              <span>🔥 {roadmap.streak} day streak</span>
              <span>✅ {completedDays} days done</span>
            </div>
            <div className="hh-rm-cta">Open Roadmap →</div>
          </div>
        </div>
      ) : (
        <div className="hh-you-section">
          <div className="hh-section-label">🗺️ recovery roadmap</div>
          <div
            className="hh-card hh-roadmap-card"
            onClick={() => setShowRoadmap(true)}
            role="button"
            tabIndex={0}
          >
            <div className="hh-rm-title">Start Your Recovery Journey</div>
            <div className="hh-rm-sub">Create a personalized plan to overcome challenges</div>
            <div className="hh-rm-cta">Create Roadmap →</div>
          </div>
        </div>
      )}

      {/* ═══ ASSESSMENTS ═══ */}
      <div className="hh-you-section">
        <div className="hh-section-label">🧪 assessments</div>
        <div className="hh-assess-card">
          {ASSESSMENTS.map(a => {
            const hist = assessHistory[a.id];
            const last = hist?.[0];
            return (
              <div
                key={a.id}
                className="hh-assess-item"
                onClick={() => setActiveTab("explore")}
              >
                <span className="hh-assess-emoji">{a.emoji}</span>
                <div className="hh-assess-info">
                  <div className="hh-assess-name">{a.name}</div>
                  <div className="hh-assess-desc">
                    {last ? `Last: ${last.date} · ${last.pct}%` : "Not yet taken"}
                  </div>
                </div>
                {last
                  ? <div className="hh-assess-score" style={{ color: a.color }}>{last.severity}</div>
                  : <span className="hh-assess-take">Take →</span>
                }
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ INSIGHT MINI ═══ */}
      <div className="hh-you-section">
        <div className="hh-section-label">📊 insight</div>
        <div
          className="hh-card hh-insight-mini"
          onClick={() => navigate("/journal")}
          role="button"
          tabIndex={0}
        >
          <div className="hh-insight-mini-title">Your week in review 📈</div>
          <div className="hh-insight-bar">
            <span className="hh-insight-label">😔 Low mood</span>
            <div className="hh-insight-track">
              <div className="hh-insight-fill" style={{ width: "40%", background: "var(--peach)" }} />
            </div>
          </div>
          <div className="hh-insight-bar">
            <span className="hh-insight-label">🌬️ Exercises</span>
            <div className="hh-insight-track">
              <div className="hh-insight-fill" style={{ width: "71%", background: "var(--sage)" }} />
            </div>
          </div>
          <div className="hh-insight-bar">
            <span className="hh-insight-label">✍️ Diary</span>
            <div className="hh-insight-track">
              <div className="hh-insight-fill" style={{ width: "57%", background: "var(--brand)" }} />
            </div>
          </div>
          <div className="hh-insight-mini-cta">See full AI insight →</div>
        </div>
      </div>

      {/* ═══ SETTINGS ═══ */}
      <div className="hh-you-section">
        <div className="hh-section-label">⚙️ settings</div>
        <div className="hh-settings-list">
          <div className="hh-setting-item" onClick={() => navigate("/profile")}>
            <span>👤 Edit Profile</span>
            <span className="hh-setting-arrow">›</span>
          </div>
          <div className="hh-setting-item" onClick={toggleDark}>
            <span>🌙 Dark Mode</span>
            <div className={`hh-toggle${isDark ? " on" : ""}`}>
              <div className="hh-toggle-dot" />
            </div>
          </div>
          <div className="hh-setting-item" onClick={() => navigate("/pricing")}>
            <span>💳 Subscription</span>
            <span className="hh-setting-arrow">›</span>
          </div>
          <div className="hh-setting-item" onClick={() => navigate("/about")}>
            <span>ℹ️ About HealHub</span>
            <span className="hh-setting-arrow">›</span>
          </div>
          <div className="hh-setting-item" onClick={handleLogout}>
            <span>🚪 Log Out</span>
            <span className="hh-setting-arrow">›</span>
          </div>
        </div>
        <div className="hh-danger-zone">
          <div className="hh-setting-item danger" onClick={() => setShowDeleteModal(true)}>
            <span>🗑️ Delete Account</span>
            <span className="hh-setting-arrow">›</span>
          </div>
        </div>
      </div>

      <div style={{ height: 24 }} />

      {/* ═══ DELETE ACCOUNT MODAL ═══ */}
      {showDeleteModal && (
        <div
          className="hh-delete-overlay"
          onClick={() => !deleting && setShowDeleteModal(false)}
        >
          <div className="hh-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="hh-delete-modal-icon">⚠️</div>
            <h3 className="hh-delete-modal-title">Delete your account?</h3>
            <p className="hh-delete-modal-desc">
              This will permanently delete your account and all your data.{" "}
              <strong>This cannot be undone.</strong>
            </p>
            <label className="hh-delete-modal-label">
              Type <strong>DELETE</strong> to confirm:
            </label>
            <input
              className="hh-delete-modal-input"
              value={deleteConfirm}
              onChange={e => { setDeleteConfirm(e.target.value); setDeleteError(""); }}
              placeholder="DELETE"
              autoFocus
              disabled={deleting}
            />
            {deleteError && (
              <div className="hh-delete-modal-error">⚠️ {deleteError}</div>
            )}
            <div className="hh-delete-modal-actions">
              <button
                className="hh-delete-cancel"
                onClick={() => { setShowDeleteModal(false); setDeleteConfirm(""); setDeleteError(""); }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="hh-delete-confirm"
                onClick={handleDeleteAccount}
                disabled={deleteConfirm !== "DELETE" || deleting}
              >
                {deleting ? "Deleting…" : "Delete Forever"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ROADMAP OVERLAY ═══
          FIX 3: uses env(safe-area-inset-top) so content clears the notch on iOS
          FIX 4: uses PWA shell CSS vars (--card, --brand, --sand-light) not desktop vars
          FIX 5: plain div instead of hh-focusroom-overlay class (doesn't exist in mobile.css)
      */}
      {showRoadmap && (
        <div style={{
          position: "fixed",
          inset: 0,
          zIndex: 99999,
          background: "var(--bg)",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}>
          <button
            onClick={() => setShowRoadmap(false)}
            style={{
              position: "fixed",
              top: `calc(env(safe-area-inset-top, 0px) + 12px)`,
              left: 16,
              zIndex: 100000,
              padding: "9px 18px",
              background: "var(--card)",
              color: "var(--brand)",
              borderRadius: 20,
              border: "1.5px solid var(--sand-light)",
              fontWeight: 700,
              fontSize: "0.88rem",
              fontFamily: "var(--font)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>

          <div style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 60px)", minHeight: "100vh" }}>
            <Roadmap isEmbeddedFullscreen />
          </div>
        </div>
      )}
    </div>
  );
}